import React, { useState, useEffect, useRef, useCallback } from 'react';
import { portalProductsAPI, portalFeedsAPI } from '../services/portalAPI';
import BarcodeScanner from './BarcodeScanner';

interface SearchResult {
  id: number;
  label_id: number;
  name: string;
  sku: string;
  upc: string;
  form: string;
  form_display: string;
  bag_size: number;
  bag_size_display: string;
  price_per_bag: number | null;
  feed_grade: string;
  animal_type: string;
  is_pellet: boolean;
}

interface RecentProduct {
  id: number;
  label_id: number | null;
  name: string;
  sku: string | null;
  upc: string | null;
  form: string;
  bag_size: number;
  price_per_bag: number | null;
  is_pellet: boolean;
  order_count: number;
}

interface QuickAddItem {
  formula_id: number;
  name: string;
  sku: string | null;
  upc: string | null;
  is_pellet: boolean;
  bag_size: number;
  quantity: number;
  price_per_bag: number | null;
}

interface POItem {
  line_number: number;
  product_name_from_po: string;
  sku_from_po: string | null;
  quantity_bags: number;
  bag_size_lbs: number | null;
  unit_cost: number | null;
  line_total: number | null;
  form: 'Mash' | 'Pellet' | null;
  matched_product_id: number | null;
  matched_product_name: string | null;
  match_confidence: 'high' | 'medium' | 'low' | 'none';
  match_notes: string;
  needs_clarification: boolean;
  clarification_question: string | null;
  clarification_options: Array<{ id: number; name: string }> | null;
  seasonal_note: string | null;
  // Local state for user selection
  user_selected_id?: number | null;
  user_confirmed?: boolean;
}

interface AvailableFeed {
  id: number;
  name: string;
  animal_type: string;
  bag_size: number;
}

interface POMetadata {
  po_number?: string;
  po_date?: string;
}

interface QuickAddProductProps {
  onAddItems: (items: QuickAddItem[], poMetadata?: POMetadata) => void;
  availableFeeds?: AvailableFeed[];
}

const QuickAddProduct: React.FC<QuickAddProductProps> = ({ onAddItems, availableFeeds: propFeeds }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [recentProducts, setRecentProducts] = useState<RecentProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SearchResult | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [searching, setSearching] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [poResults, setPOResults] = useState<{ items: POItem[]; po_number?: string; po_date?: string; parsing_notes?: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [localFeeds, setLocalFeeds] = useState<AvailableFeed[]>([]);

  // Use prop feeds if provided, otherwise use locally loaded feeds
  const availableFeeds = propFeeds || localFeeds;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load recent products and available feeds on mount
  useEffect(() => {
    loadRecentProducts();
    // Only load feeds if not provided via props
    if (!propFeeds) {
      loadAvailableFeeds();
    }
  }, [propFeeds]);

  const loadAvailableFeeds = async () => {
    try {
      const response = await portalFeedsAPI.getAll();
      setLocalFeeds(response.data || []);
    } catch (err) {
      console.error('Failed to load available feeds:', err);
    }
  };

  // Auto-search when query changes
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadRecentProducts = async () => {
    try {
      const response = await portalProductsAPI.getRecent();
      setRecentProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to load recent products:', err);
    }
  };

  const performSearch = async (query: string) => {
    setSearching(true);
    setError('');
    try {
      const response = await portalProductsAPI.search(query);
      setSearchResults(response.data.results || []);

      // If UPC and exactly one result, auto-select it
      if (response.data.is_upc && response.data.results?.length === 1) {
        handleSelectProduct(response.data.results[0]);
      }
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectProduct = (product: SearchResult) => {
    setSelectedProduct(product);
    setQuantity('1');
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleAddToOrder = () => {
    if (!selectedProduct) return;

    const qty = parseInt(quantity) || 1;
    const item: QuickAddItem = {
      formula_id: selectedProduct.id,
      name: selectedProduct.name,
      sku: selectedProduct.sku,
      upc: selectedProduct.upc,
      is_pellet: selectedProduct.is_pellet,
      bag_size: selectedProduct.bag_size,
      quantity: Math.max(1, qty),
      price_per_bag: selectedProduct.price_per_bag,
    };

    onAddItems([item]);
    setSelectedProduct(null);
    setQuantity('1');

    // Focus back on search field
    searchInputRef.current?.focus();
  };

  const handleQuickAdd = (product: RecentProduct, qty: number) => {
    const item: QuickAddItem = {
      formula_id: product.id,
      name: product.name,
      sku: product.sku,
      upc: product.upc,
      is_pellet: product.is_pellet,
      bag_size: product.bag_size,
      quantity: qty,
      price_per_bag: product.price_per_bag,
    };
    onAddItems([item]);
  };

  const handleBarcodeScan = useCallback((code: string) => {
    setShowScanner(false);
    setSearchQuery(code);
    performSearch(code);
  }, []);

  const handlePOUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const response = await portalProductsAPI.parsePurchaseOrder(file);
      // Initialize user_confirmed for all items
      const items = (response.data.items || []).map((item: POItem) => ({
        ...item,
        user_confirmed: item.match_confidence === 'high' && !item.needs_clarification,
        user_selected_id: item.matched_product_id,
      }));
      setPOResults({ ...response.data, items });
      setShowPOModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to parse purchase order');
    } finally {
      setUploading(false);
      // Clear file input
      e.target.value = '';
    }
  };

  const handlePOItemSelect = (lineNumber: number, productId: number, productName: string) => {
    if (!poResults) return;
    setPOResults({
      ...poResults,
      items: poResults.items.map((item) =>
        item.line_number === lineNumber
          ? {
              ...item,
              user_selected_id: productId,
              matched_product_id: productId,
              matched_product_name: productName,
              user_confirmed: true,
              needs_clarification: false,
              match_confidence: 'high' as const,
            }
          : item
      ),
    });
  };

  const handlePOItemConfirm = (lineNumber: number) => {
    if (!poResults) return;
    setPOResults({
      ...poResults,
      items: poResults.items.map((item) =>
        item.line_number === lineNumber ? { ...item, user_confirmed: true } : item
      ),
    });
  };

  const handlePOItemSkip = (lineNumber: number) => {
    if (!poResults) return;
    setPOResults({
      ...poResults,
      items: poResults.items.filter((item) => item.line_number !== lineNumber),
    });
  };

  const handleManualMatch = (lineNumber: number, feedId: number) => {
    if (!poResults || !feedId) return;
    const feed = availableFeeds.find((f) => f.id === feedId);
    if (!feed) return;

    setPOResults({
      ...poResults,
      items: poResults.items.map((item) =>
        item.line_number === lineNumber
          ? {
              ...item,
              matched_product_id: feedId,
              matched_product_name: feed.name,
              match_confidence: 'high' as const,
              needs_clarification: false,
              user_confirmed: true,
              match_notes: 'Manually selected by user',
            }
          : item
      ),
    });
  };

  // Get unique animal types from available feeds for grouping
  const getAnimalTypes = () => {
    const types = new Set(availableFeeds.map((f) => f.animal_type));
    return Array.from(types).sort();
  };

  const getConfirmedItemCount = () => {
    if (!poResults?.items) return 0;
    return poResults.items.filter(
      (item) => item.user_confirmed && item.matched_product_id
    ).length;
  };

  const getPendingItemCount = () => {
    if (!poResults?.items) return 0;
    return poResults.items.filter(
      (item) => !item.user_confirmed || !item.matched_product_id
    ).length;
  };

  const allPOItemsResolved = () => {
    if (!poResults?.items) return false;
    return poResults.items.every(
      (item) => item.user_confirmed && item.matched_product_id
    );
  };

  const handleAddAllFromPO = () => {
    if (!poResults?.items) return;

    // Only add confirmed items with valid product IDs
    const confirmedItems = poResults.items.filter(
      (item) => item.user_confirmed && item.matched_product_id
    );

    const items: QuickAddItem[] = confirmedItems.map((item) => ({
      formula_id: item.matched_product_id!,
      name: item.matched_product_name || item.product_name_from_po,
      sku: item.sku_from_po,
      upc: null,
      is_pellet: item.form === 'Pellet',
      bag_size: item.bag_size_lbs || 40,
      quantity: item.quantity_bags,
      price_per_bag: null, // Will be calculated by parent
    }));

    // Pass PO metadata along with items
    const poMetadata: POMetadata = {
      po_number: poResults.po_number,
      po_date: poResults.po_date,
    };

    onAddItems(items, poMetadata);
    setShowPOModal(false);
    setPOResults(null);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Quick Add Product</h2>

      {error && <div style={styles.error}>{error}</div>}

      {/* Search Bar */}
      <div style={styles.searchRow}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>
            {searching ? '...' : ''}
          </span>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by SKU, UPC, or product name..."
            style={styles.searchInput}
          />
        </div>
        <button
          onClick={() => setShowScanner(true)}
          style={styles.scanButton}
          title="Scan Barcode"
        >
          Scan
        </button>
      </div>

      {/* Search Results Dropdown */}
      {searchResults.length > 0 && (
        <div style={styles.resultsDropdown}>
          {searchResults.map((product) => (
            <div
              key={`${product.id}-${product.form}`}
              style={styles.resultItem}
              onClick={() => handleSelectProduct(product)}
            >
              <div style={styles.resultName}>{product.name}</div>
              <div style={styles.resultDetails}>
                <span style={styles.resultSku}>{product.sku}</span>
                <span style={styles.resultForm}>{product.form_display}</span>
                <span style={styles.resultSize}>{product.bag_size_display}</span>
                {product.price_per_bag && (
                  <span style={styles.resultPrice}>${product.price_per_bag.toFixed(2)}/bag</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Product */}
      {selectedProduct && (
        <div style={styles.selectedProduct}>
          <div style={styles.selectedInfo}>
            <div style={styles.selectedName}>{selectedProduct.name}</div>
            <div style={styles.selectedDetails}>
              <span>{selectedProduct.form_display}</span>
              <span>{selectedProduct.bag_size_display}</span>
              {selectedProduct.sku && <span>SKU: {selectedProduct.sku}</span>}
            </div>
            {selectedProduct.price_per_bag && (
              <div style={styles.selectedPrice}>
                ${selectedProduct.price_per_bag.toFixed(2)} per bag
              </div>
            )}
          </div>
          <div style={styles.quantityRow}>
            <label style={styles.quantityLabel}>Qty (bags):</label>
            <input
              type="text"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onBlur={() => {
                const val = parseInt(quantity) || 1;
                setQuantity(Math.max(1, val).toString());
              }}
              style={styles.quantityInput}
            />
            <button onClick={handleAddToOrder} style={styles.addButton}>
              Add to Order
            </button>
          </div>
        </div>
      )}

      {/* PO Upload */}
      <div style={styles.poUploadSection}>
        <span style={styles.dividerText}>or upload your Purchase Order</span>
        <label style={styles.poUploadButton}>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handlePOUpload}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          {uploading ? 'Processing...' : 'Upload PO (PDF/Image)'}
        </label>
      </div>

      {/* Recent Products */}
      {recentProducts.length > 0 && !selectedProduct && searchResults.length === 0 && (
        <div style={styles.recentSection}>
          <h3 style={styles.recentTitle}>Your Recent Products</h3>
          <div style={styles.recentGrid}>
            {recentProducts.slice(0, 6).map((product) => (
              <div key={`${product.id}-${product.form}`} style={styles.recentCard}>
                <div style={styles.recentName}>{product.name}</div>
                <div style={styles.recentSku}>{product.sku || product.form}</div>
                <div style={styles.quickAddButtons}>
                  <button
                    onClick={() => handleQuickAdd(product, 1)}
                    style={styles.quickAddBtn}
                  >
                    +1
                  </button>
                  <button
                    onClick={() => handleQuickAdd(product, 5)}
                    style={styles.quickAddBtn}
                  >
                    +5
                  </button>
                  <button
                    onClick={() => handleQuickAdd(product, 10)}
                    style={styles.quickAddBtn}
                  >
                    +10
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* PO Results Modal */}
      {showPOModal && poResults && (
        <div style={styles.modalOverlay}>
          <div style={styles.poModal}>
            <div style={styles.poModalHeader}>
              <h3 style={styles.modalTitle}>Purchase Order Review</h3>
              {poResults.po_number && (
                <span style={styles.poNumber}>PO #{poResults.po_number}</span>
              )}
            </div>

            {poResults.items?.length > 0 ? (
              <>
                <div style={styles.poItemsList}>
                  {poResults.items.map((item) => {
                    // Determine item status color
                    let statusColor = '#F3F4F6'; // Default gray
                    let statusIcon = '○';
                    if (item.user_confirmed && item.matched_product_id) {
                      statusColor = '#D1FAE5'; // Green - confirmed
                      statusIcon = '✓';
                    } else if (item.needs_clarification) {
                      statusColor = '#FEF3C7'; // Yellow - needs selection
                      statusIcon = '?';
                    } else if (item.match_confidence === 'none' || !item.matched_product_id) {
                      statusColor = '#FEE2E2'; // Red - no match
                      statusIcon = '✕';
                    } else if (item.match_confidence === 'medium' || item.match_confidence === 'low') {
                      statusColor = '#DBEAFE'; // Blue - low confidence
                      statusIcon = '~';
                    }

                    return (
                      <div
                        key={item.line_number}
                        style={{ ...styles.poItem, backgroundColor: statusColor }}
                      >
                        <div style={styles.poItemHeader}>
                          <span style={styles.poItemStatus}>{statusIcon}</span>
                          <span style={styles.poItemName}>{item.product_name_from_po}</span>
                          <span style={styles.poItemQty}>{item.quantity_bags} bags</span>
                        </div>

                        {item.sku_from_po && (
                          <div style={styles.poItemSku}>SKU: {item.sku_from_po}</div>
                        )}

                        {/* Show match info */}
                        {item.matched_product_name && item.user_confirmed && (
                          <div style={styles.poItemMatch}>
                            → {item.matched_product_name}
                          </div>
                        )}

                        {/* Seasonal note */}
                        {item.seasonal_note && (
                          <div style={styles.poItemSeasonal}>
                            🔄 {item.seasonal_note}
                          </div>
                        )}

                        {/* Clarification needed - show options */}
                        {item.needs_clarification && item.clarification_options && item.clarification_options.length > 0 && (
                          <div style={styles.clarificationSection}>
                            <div style={styles.clarificationQuestion}>
                              {item.clarification_question || 'Which product did you mean?'}
                            </div>
                            <div style={styles.clarificationOptions}>
                              {item.clarification_options.map((opt) => (
                                <button
                                  key={opt.id}
                                  onClick={() => handlePOItemSelect(item.line_number, opt.id, opt.name)}
                                  style={styles.clarificationBtn}
                                >
                                  {opt.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Clarification needed but no options - show manual selector */}
                        {item.needs_clarification && (!item.clarification_options || item.clarification_options.length === 0) && !item.user_confirmed && (
                          <div style={styles.manualSelectSection}>
                            <span style={styles.noMatchText}>
                              {item.clarification_question || 'Select the correct product:'}
                            </span>
                            <select
                              onChange={(e) => handleManualMatch(item.line_number, parseInt(e.target.value))}
                              defaultValue=""
                              style={styles.manualSelectDropdown}
                            >
                              <option value="" disabled>Select correct product...</option>
                              {getAnimalTypes().map((animalType) => (
                                <optgroup key={animalType} label={animalType}>
                                  {availableFeeds
                                    .filter((f) => f.animal_type === animalType)
                                    .map((feed) => (
                                      <option key={feed.id} value={feed.id}>
                                        {feed.name} ({feed.bag_size} lb)
                                      </option>
                                    ))}
                                </optgroup>
                              ))}
                            </select>
                            <button
                              onClick={() => handlePOItemSkip(item.line_number)}
                              style={styles.skipBtn}
                            >
                              Skip Item
                            </button>
                          </div>
                        )}

                        {/* No match - show manual selector */}
                        {item.match_confidence === 'none' && !item.needs_clarification && !item.user_confirmed && (
                          <div style={styles.manualSelectSection}>
                            <span style={styles.noMatchText}>
                              {item.match_notes || 'Could not match to your catalog'}
                            </span>
                            <select
                              onChange={(e) => handleManualMatch(item.line_number, parseInt(e.target.value))}
                              defaultValue=""
                              style={styles.manualSelectDropdown}
                            >
                              <option value="" disabled>Select correct product...</option>
                              {getAnimalTypes().map((animalType) => (
                                <optgroup key={animalType} label={animalType}>
                                  {availableFeeds
                                    .filter((f) => f.animal_type === animalType)
                                    .map((feed) => (
                                      <option key={feed.id} value={feed.id}>
                                        {feed.name} ({feed.bag_size} lb)
                                      </option>
                                    ))}
                                </optgroup>
                              ))}
                            </select>
                            <button
                              onClick={() => handlePOItemSkip(item.line_number)}
                              style={styles.skipBtn}
                            >
                              Skip Item
                            </button>
                          </div>
                        )}

                        {/* Low/medium confidence - confirm button */}
                        {!item.user_confirmed &&
                          item.matched_product_id &&
                          !item.needs_clarification &&
                          (item.match_confidence === 'medium' || item.match_confidence === 'low') && (
                            <div style={styles.confirmSection}>
                              <span style={styles.matchNote}>
                                Matched to: {item.matched_product_name}
                              </span>
                              <div style={styles.confirmButtons}>
                                <button
                                  onClick={() => handlePOItemConfirm(item.line_number)}
                                  style={styles.confirmBtn}
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => handlePOItemSkip(item.line_number)}
                                  style={styles.skipBtn}
                                >
                                  Skip
                                </button>
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>

                <div style={styles.poModalFooter}>
                  {getPendingItemCount() > 0 && getConfirmedItemCount() > 0 && (
                    <div style={styles.partialWarning}>
                      {getPendingItemCount()} item{getPendingItemCount() !== 1 ? 's' : ''} still need{getPendingItemCount() === 1 ? 's' : ''} attention
                    </div>
                  )}
                  <button
                    onClick={handleAddAllFromPO}
                    style={{
                      ...styles.addAllButton,
                      opacity: getConfirmedItemCount() > 0 ? 1 : 0.5,
                      cursor: getConfirmedItemCount() > 0 ? 'pointer' : 'not-allowed',
                    }}
                    disabled={getConfirmedItemCount() === 0}
                  >
                    {getConfirmedItemCount() > 0
                      ? `Add ${getConfirmedItemCount()} Confirmed Item${getConfirmedItemCount() !== 1 ? 's' : ''} to Order${getPendingItemCount() > 0 ? ` (${getPendingItemCount()} skipped)` : ''}`
                      : 'No Items Ready to Add'}
                  </button>
                  <button onClick={() => setShowPOModal(false)} style={styles.closeButton}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div style={styles.noItems}>
                <p>No items found in the purchase order.</p>
                {poResults.parsing_notes && (
                  <p style={styles.parsingNotes}>{poResults.parsing_notes}</p>
                )}
                <button onClick={() => setShowPOModal(false)} style={styles.closeButton}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 1rem 0',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  searchRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  searchWrapper: {
    flex: 1,
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  scanButton: {
    padding: '0.75rem 1.25rem',
    backgroundColor: '#508590',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  resultsDropdown: {
    position: 'absolute',
    left: '1.5rem',
    right: '1.5rem',
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    maxHeight: '300px',
    overflowY: 'auto',
    zIndex: 100,
  },
  resultItem: {
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    borderBottom: '1px solid #e5e5e5',
  },
  resultName: {
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '4px',
  },
  resultDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.875rem',
    color: '#666',
  },
  resultSku: {
    color: '#508590',
    fontFamily: 'monospace',
  },
  resultForm: {
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '3px',
  },
  resultSize: {},
  resultPrice: {
    color: '#6C8062',
    fontWeight: '500',
  },
  selectedProduct: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#F5F9F5',
    border: '2px solid #6C8062',
    borderRadius: '8px',
  },
  selectedInfo: {},
  selectedName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '4px',
  },
  selectedDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: '8px',
  },
  selectedPrice: {
    color: '#6C8062',
    fontWeight: '600',
    fontSize: '1rem',
  },
  quantityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  quantityLabel: {
    fontWeight: '500',
    color: '#3F2021',
  },
  quantityInput: {
    width: '80px',
    padding: '0.5rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '1rem',
    textAlign: 'center',
  },
  addButton: {
    padding: '0.5rem 1.5rem',
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  poUploadSection: {
    marginTop: '1.5rem',
    textAlign: 'center',
    paddingTop: '1rem',
    borderTop: '1px dashed #C3AD8B',
  },
  dividerText: {
    display: 'block',
    color: '#666',
    marginBottom: '0.75rem',
    fontSize: '0.875rem',
  },
  poUploadButton: {
    display: 'inline-block',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#FAF9F6',
    border: '2px dashed #C3AD8B',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#3F2021',
    fontWeight: '500',
  },
  recentSection: {
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e5e5',
  },
  recentTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
    marginBottom: '0.75rem',
  },
  recentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '0.75rem',
  },
  recentCard: {
    padding: '0.75rem',
    border: '1px solid #e5e5e5',
    borderRadius: '6px',
    backgroundColor: '#FAF9F6',
  },
  recentName: {
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#3F2021',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  recentSku: {
    fontSize: '0.75rem',
    color: '#508590',
    fontFamily: 'monospace',
    marginBottom: '8px',
  },
  quickAddButtons: {
    display: 'flex',
    gap: '4px',
  },
  quickAddBtn: {
    flex: 1,
    padding: '4px 8px',
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  poModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '95%',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
  poModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #e5e5e5',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#3F2021',
    margin: 0,
  },
  poNumber: {
    fontSize: '0.875rem',
    color: '#508590',
    fontFamily: 'monospace',
  },
  poItemsList: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem 1.5rem',
  },
  poItem: {
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '0.75rem',
    border: '1px solid rgba(0,0,0,0.1)',
  },
  poItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  poItemStatus: {
    fontSize: '1rem',
    fontWeight: '600',
    width: '24px',
    textAlign: 'center',
  },
  poItemName: {
    flex: 1,
    fontWeight: '600',
    color: '#3F2021',
    fontSize: '0.9375rem',
  },
  poItemQty: {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: '500',
  },
  poItemSku: {
    fontSize: '0.75rem',
    color: '#508590',
    fontFamily: 'monospace',
    marginLeft: '2rem',
    marginTop: '4px',
  },
  poItemMatch: {
    fontSize: '0.875rem',
    color: '#059669',
    marginLeft: '2rem',
    marginTop: '4px',
  },
  poItemSeasonal: {
    fontSize: '0.8125rem',
    color: '#2563EB',
    marginLeft: '2rem',
    marginTop: '4px',
    padding: '4px 8px',
    backgroundColor: '#EFF6FF',
    borderRadius: '4px',
    display: 'inline-block',
  },
  clarificationSection: {
    marginTop: '0.75rem',
    marginLeft: '2rem',
  },
  clarificationQuestion: {
    fontSize: '0.875rem',
    color: '#92400E',
    marginBottom: '0.5rem',
  },
  clarificationOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  clarificationBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#FFFFFF',
    border: '2px solid #F59E0B',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
    color: '#92400E',
  },
  noMatchSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.75rem',
    marginLeft: '2rem',
  },
  noMatchText: {
    fontSize: '0.8125rem',
    color: '#991B1B',
  },
  manualSelectSection: {
    marginTop: '0.75rem',
    marginLeft: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  manualSelectDropdown: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid #C3AD8B',
    fontSize: '0.875rem',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
  },
  confirmSection: {
    marginTop: '0.75rem',
    marginLeft: '2rem',
  },
  matchNote: {
    fontSize: '0.8125rem',
    color: '#1E40AF',
    display: 'block',
    marginBottom: '0.5rem',
  },
  confirmButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  confirmBtn: {
    padding: '0.375rem 1rem',
    backgroundColor: '#059669',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
  },
  skipBtn: {
    padding: '0.375rem 0.75rem',
    backgroundColor: '#FFFFFF',
    color: '#666',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
  },
  poModalFooter: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e5e5',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  noItems: {
    padding: '2rem 1.5rem',
    textAlign: 'center',
    color: '#666',
  },
  parsingNotes: {
    fontSize: '0.875rem',
    color: '#92400E',
    backgroundColor: '#FEF3C7',
    padding: '0.75rem',
    borderRadius: '6px',
    marginTop: '1rem',
  },
  addAllButton: {
    width: '100%',
    padding: '0.875rem',
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
  },
  closeButton: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#FAF9F6',
    color: '#3F2021',
    border: '2px solid #C3AD8B',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  partialWarning: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#92400E',
    backgroundColor: '#FEF3C7',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  },
};

export default QuickAddProduct;
